import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIncidents, Incident, IncidentIssue, Incidents } from '../../api/incidents';
import { Stage } from '../../api/issues';
import { describeError } from '../../api/requeue';
import { useApiAuth, useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { IdChip } from '../Money/IdText';
import './IncidentsPanel.scss';

const DAYS = 14;

/** After this long an open message of an outage did not come right by itself: it needs a look. */
const STUCK_AFTER_MS = 24 * 3_600_000;

type Tone = 'done' | 'open' | 'stuck';

const toneOf = (incident: Incident, now: number): Tone =>
    incident.open === 0 ? 'done' : now - new Date(incident.end).getTime() > STUCK_AFTER_MS ? 'stuck' : 'open';

const TONE_LABEL: Record<Tone, string> = {
    done: 'alles later gelukt',
    open: 'nog niet alles door',
    stuck: 'niet vanzelf goed gekomen',
};

const two = (n: number) => String(n).padStart(2, '0');
const clock = (iso: string) => {
    const d = new Date(iso);

    return `${two(d.getHours())}:${two(d.getMinutes())}`;
};
const dayName = (d: Date) => d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });

/** "do 3 okt 04:05–04:10". */
const span = (incident: Incident) => `${dayName(new Date(incident.start))} ${clock(incident.start)}${incident.end !== incident.start ? `–${clock(incident.end)}` : ''}`;

export interface IIncidentsPanelProps {
    stage?: Stage | '';
    /** Opens the issues of one message. */
    onOpenIssue: (issue: IncidentIssue) => void;
}

/**
 * The times Exact was out of reach, as incidents instead of piles of issues: two weeks as a strip of days with a bar
 * per outage, and a card for every outage that did not get through completely afterwards. What did get through is one
 * line: it happened, it came right, nothing to do.
 */
export const IncidentsPanel = ({ stage, onOpenIssue }: IIncidentsPanelProps) => {
    const anyAxios = useAxiosSwagger();
    const { loading: tokenLoading } = useApiAuth();
    const axios = tokenLoading ? undefined : anyAxios;
    const [data, setData] = useState<Incidents | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showDone, setShowDone] = useState<boolean>(false);

    useEffect(() => {
        if (!axios) {
            return;
        }

        const controller = new AbortController();

        getIncidents(axios, DAYS, stage, controller.signal)
            .then((result) => {
                setData(result);
                setError(null);
            })
            .catch((ex) => {
                if (!controller.signal.aborted) {
                    setError(describeError(ex));
                }
            });

        return () => controller.abort();
    }, [axios, stage]);

    const now = Date.now();
    const incidents = useMemo(() => data?.incidents ?? [], [data]);
    const pending = incidents.filter((i) => i.open > 0);
    const done = incidents.filter((i) => i.open === 0);

    if (error) {
        return <p className="issues__error">Storingen konden niet gelezen worden: {error}</p>;
    }

    if (!data || incidents.length === 0) {
        return null;
    }

    return (
        <section className="incidents" aria-label="Storingen bij Exact">
            <header className="incidents__head">
                <h4 className="incidents__title">
                    <FontAwesomeIcon icon="bolt" /> Storingen bij Exact, laatste {DAYS} dagen
                </h4>
                <span className="incidents__sum">
                    {incidents.length} storingen, {incidents.reduce((n, i) => n + i.count, 0)} berichten,{' '}
                    {pending.length === 0 ? 'alles later gelukt' : `${pending.reduce((n, i) => n + i.open, 0)} nog niet door`}
                </span>
            </header>

            <DayStrip incidents={incidents} from={data.from} now={now} />

            {pending.map((incident) => (
                <IncidentCard key={incident.id} incident={incident} tone={toneOf(incident, now)} onOpenIssue={onOpenIssue} />
            ))}

            {done.length > 0 && (
                <div className="incidents__done">
                    <button type="button" className="btn btn-sm btn-link" onClick={() => setShowDone(!showDone)}>
                        <FontAwesomeIcon icon={showDone ? 'angle-down' : 'angle-right'} /> <FontAwesomeIcon icon="circle-check" className="incidents__ok" />{' '}
                        {done.length} {done.length === 1 ? 'storing' : 'storingen'} waarbij alles later gelukt is ({done.reduce((n, i) => n + i.count, 0)} berichten)
                    </button>
                    {showDone && done.map((incident) => <IncidentCard key={incident.id} incident={incident} tone="done" onOpenIssue={onOpenIssue} />)}
                </div>
            )}
        </section>
    );
};

/** Two weeks as columns of days, a bar per outage: the height is how many messages, the colour whether they got through. */
const DayStrip = ({ incidents, from, now }: { incidents: Incident[]; from: string; now: number }) => {
    const start = new Date(from);
    start.setHours(0, 0, 0, 0);
    const days = Array.from({ length: DAYS + 1 }, (_, i) => new Date(start.getTime() + i * 86_400_000)).filter((d) => d.getTime() <= now);
    const max = Math.max(1, ...incidents.map((i) => i.count));

    return (
        <div className="incidents__strip" role="img" aria-label="Storingen per dag">
            {days.map((day) => {
                const next = day.getTime() + 86_400_000;
                const ofDay = incidents.filter((i) => {
                    const at = new Date(i.start).getTime();

                    return at >= day.getTime() && at < next;
                });

                return (
                    <div key={day.toISOString()} className="incidents__day">
                        <div className="incidents__bars">
                            {ofDay.map((incident) => (
                                <span
                                    key={incident.id}
                                    className={`incidents__bar incidents__bar--${toneOf(incident, now)}`}
                                    style={{ height: `${Math.max(12, (incident.count / max) * 100)}%` }}
                                    title={`${span(incident)}: ${incident.title}. ${incident.count} berichten, ${incident.resolved} later gelukt, ${incident.open} nog open.`}
                                />
                            ))}
                        </div>
                        <span className="incidents__day-label">{day.toLocaleDateString('nl-NL', { weekday: 'narrow', day: 'numeric' })}</span>
                    </div>
                );
            })}
        </div>
    );
};

const IncidentCard = ({ incident, tone, onOpenIssue }: { incident: Incident; tone: Tone; onOpenIssue: (issue: IncidentIssue) => void }) => {
    const max = Math.max(1, ...incident.perMinute);
    const share = incident.count === 0 ? 0 : (100 * incident.resolved) / incident.count;

    return (
        <article className={`incident incident--${tone}`}>
            <header className="incident__head">
                <span className="incident__when">{span(incident)}</span>
                <span className="incident__title">{incident.title}</span>
                <span className={`incident__state incident__state--${tone}`}>
                    <FontAwesomeIcon icon={tone === 'done' ? 'circle-check' : tone === 'stuck' ? 'circle-exclamation' : 'hourglass-half'} /> {TONE_LABEL[tone]}
                </span>
            </header>

            <div className="incident__body">
                <div className="incident__spark" role="img" aria-label="Mislukte berichten per minuut">
                    {incident.perMinute.map((n, i) => (
                        <span key={i} className="incident__spark-bar" style={{ height: `${n === 0 ? 0 : Math.max(10, (n / max) * 100)}%` }} title={`${n} in minuut ${i + 1}`} />
                    ))}
                </div>

                <div className="incident__progress">
                    <div className="incident__track">
                        <span className="incident__fill" style={{ width: `${share}%` }} />
                    </div>
                    <span className="incident__count">
                        <strong>{incident.resolved}</strong> van {incident.count} later gelukt{incident.open > 0 ? `, ${incident.open} nog open` : ''}
                    </span>
                </div>
            </div>

            <div className="incident__parts">
                {incident.environments.map((env) => (
                    <span key={env.key} className={`incident__chip${env.open > 0 ? ' incident__chip--open' : ''}`} title={`${env.count} berichten, ${env.open} nog open`}>
                        {env.title} <strong>{env.open > 0 ? `${env.open}/${env.count}` : env.count}</strong>
                    </span>
                ))}
                {incident.codes.map((code) => (
                    <span key={code.key} className="incident__chip incident__chip--code">
                        {code.title} <strong>{code.count}</strong>
                    </span>
                ))}
            </div>

            {incident.openIssues.length > 0 && (
                <ul className="incident__open">
                    {incident.openIssues.map((issue) => (
                        <li key={`${issue.customer}/${issue.environment}/${issue.key}`}>
                            <IdChip id={issue.relationId} system="chargebee" />
                            <span className="incident__type">{issue.objectType}</span>
                            <span className="incident__env">
                                {issue.customer}/{issue.environment}
                            </span>
                            <button type="button" className="btn btn-sm btn-link" onClick={() => onOpenIssue(issue)}>
                                Bekijk
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </article>
    );
};
